// src/components/staff/mobile/MobileStaffChat.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Send, Hash, Lock, User, Search, MessageSquare, CheckCheck, ChevronLeft
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { chatService } from '../../../services/chatService';

const GLOBAL_STYLE = `@keyframes spin { to { transform: rotate(360deg) } }`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  const diff = Date.now() - date;
  if (diff < 60000)    return 'Just now';
  if (diff < 3600000)  return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return date.toLocaleDateString();
};

// Icône selon le type de room — identique à ChatView.js getRoomIcon()
const RoomIcon = ({ room, size = 14 }) => {
  if (room.room_type === 'private') return <Lock size={size} />;
  if (room.room_type === 'direct')  return <User size={size} />;
  return <Hash size={size} />;
};

// ─── Message Bubble ───────────────────────────────────────────────────────────
const MessageBubble = ({ msg, isOwn }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start', animation: 'fadeIn 0.2s ease' }}>
    {/* Nom de l'expéditeur — visible seulement pour les messages des autres */}
    {!isOwn && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <div style={{ width: 22, height: 22, borderRadius: 7, background: 'rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#60a5fa' }}>
          {msg.first_name?.[0]}{msg.last_name?.[0]}
        </div>
        {/* first_name + last_name exactement comme ChatView.js ligne 146 */}
        <span style={{ fontSize: 11, color: '#60a5fa', fontWeight: 600 }}>
          {msg.first_name} {msg.last_name}
        </span>
        <span style={{ fontSize: 10, color: '#475569' }}>{formatTime(msg.created_at)}</span>
      </div>
    )}

    <div style={{
      maxWidth: '78%',
      background: isOwn ? '#2563eb' : '#1e293b',
      borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
      padding: '10px 14px',
      border: isOwn ? 'none' : '0.5px solid #334155',
    }}>
      <p style={{ fontSize: 14, color: '#fff', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
        {msg.message}
      </p>
    </div>

    {isOwn && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
        <span style={{ fontSize: 10, color: '#475569' }}>{formatTime(msg.created_at)}</span>
      </div>
    )}
  </div>
);

// ─── Room Item dans la liste ──────────────────────────────────────────────────
const RoomItem = ({ room, active, onClick }) => (
  <button onClick={onClick} style={{
    width: '100%', background: active ? 'rgba(59,130,246,0.12)' : 'none',
    border: 'none', borderRadius: 14, padding: '12px 14px',
    cursor: 'pointer', textAlign: 'left',
    display: 'flex', alignItems: 'center', gap: 12,
    outline: active ? '1px solid rgba(59,130,246,0.3)' : 'none',
  }}>
    {/* Avatar avec icône du type de room */}
    <div style={{
      width: 42, height: 42, borderRadius: 13, flexShrink: 0,
      background: active ? 'rgba(59,130,246,0.2)' : '#1e293b',
      border: '0.5px solid #334155',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: active ? '#60a5fa' : '#475569',
    }}>
      <RoomIcon room={room} size={16} />
    </div>

    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
        {/* Nom du channel — room.room_name avec préfixe # comme ChatView */}
        <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
          {room.room_type === 'channel' ? '#' : ''}{room.room_name}
        </span>
        {room.last_message_at && (
          <span style={{ fontSize: 10, color: '#475569', flexShrink: 0 }}>
            {formatTime(room.last_message_at)}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {room.last_message || 'No messages yet'}
        </span>
        {room.unread_count > 0 && (
          <div style={{ width: 18, height: 18, borderRadius: 9, background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>
              {room.unread_count > 9 ? '9+' : room.unread_count}
            </span>
          </div>
        )}
      </div>
      {/* Description si disponible */}
      {room.description && (
        <span style={{ fontSize: 10, color: '#334155', marginTop: 1, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {room.description}
        </span>
      )}
    </div>
  </button>
);

// ─── Conversation View ────────────────────────────────────────────────────────
const ConversationView = ({ room, user, onBack }) => {
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState('');
  const [sending, setSending]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const wsRef                     = useRef(null);
  const bottomRef                 = useRef(null);
  const inputRef                  = useRef(null);

  useEffect(() => {
    fetchMessages();
    // WebSocket — identique à ChatView.js connectWebSocket()
    wsRef.current = chatService.connectWebSocket(room.id, user.id, (message) => {
      setMessages(prev => [...prev, {
        ...message,
        // Fallback identique à ChatView.js ligne 240-242
        first_name: message.first_name || message.sender_name?.split(' ')[0] || 'User',
        last_name:  message.last_name  || message.sender_name?.split(' ')[1] || '',
      }]);
    });
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [room.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const data = await chatService.getMessages(room.id);
      setMessages(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    const text = input.trim();
    setInput('');
    try {
      // Identique à ChatView.js sendMessage()
      await chatService.sendMessage(room.id, text);
      await fetchMessages();
    } catch (e) {
      console.error('Error sending message:', e);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    // Identique à ChatView.js handleKeyPress() — Enter seulement sur desktop
    if (e.key === 'Enter' && !e.shiftKey && window.innerWidth >= 640) {
      e.preventDefault();
      send();
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    // Auto-resize identique à ChatView.js
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 100) + 'px';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 56px - env(safe-area-inset-top) - 72px)' }}>
      {/* Header avec nom du channel */}
      <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '0.5px solid #1e293b', flexShrink: 0, background: '#0f172a' }}>
        <button onClick={onBack} style={{ background: '#1e293b', border: '0.5px solid #334155', borderRadius: 10, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
          <ChevronLeft size={16} color="#94a3b8" />
        </button>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60a5fa', flexShrink: 0 }}>
          <RoomIcon room={room} size={15} />
        </div>
        <div style={{ flex: 1 }}>
          {/* Nom du channel avec # identique à ChatView */}
          <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>
            {room.room_type === 'channel' ? '#' : ''}{room.room_name}
          </div>
          {room.description && (
            <div style={{ fontSize: 11, color: '#475569', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {room.description}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
            <div style={{ width: 22, height: 22, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          </div>
        ) : messages.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#475569', gap: 8 }}>
            <MessageSquare size={32} color="#334155" />
            <p style={{ fontSize: 13 }}>No messages yet in {room.room_type === 'channel' ? '#' : ''}{room.room_name}</p>
          </div>
        ) : messages.map((msg, i) => {
          // isOwn : même logique que ChatView.js
          const isOwn = msg.sender_id === user.id || msg.staff_id === user.id;
          return <MessageBubble key={msg.id || i} msg={msg} isOwn={isOwn} />;
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <div style={{ padding: '10px 12px', paddingBottom: 'max(10px, env(safe-area-inset-bottom))', borderTop: '0.5px solid #1e293b', display: 'flex', alignItems: 'flex-end', gap: 8, background: 'rgba(15,23,42,0.97)', flexShrink: 0 }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={`Message ${room.room_type === 'channel' ? '#' : ''}${room.room_name}…`}
          rows={1}
          style={{ flex: 1, background: '#1e293b', border: '0.5px solid #334155', borderRadius: 20, padding: '10px 14px', color: '#f1f5f9', fontSize: 15, resize: 'none', outline: 'none', maxHeight: 100, overflowY: 'auto', lineHeight: 1.4, fontFamily: '-apple-system,BlinkMacSystemFont,sans-serif' }}
        />
        <button onClick={send} disabled={sending || !input.trim()} style={{ width: 40, height: 40, borderRadius: 20, flexShrink: 0, background: input.trim() ? '#2563eb' : '#1e293b', border: 'none', cursor: input.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}>
          {sending
            ? <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            : <Send size={17} color={input.trim() ? '#fff' : '#475569'} />
          }
        </button>
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const MobileStaffChat = () => {
  const { user }                  = useAuth();
  const [rooms, setRooms]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [activeRoom, setActiveRoom] = useState(null);

  useEffect(() => {
    // Identique à ChatView.js fetchRooms()
    chatService.getRooms()
      .then(data => {
        setRooms(data);
        // Auto-sélectionner le premier room comme ChatView.js ligne 218
        if (data.length > 0) setActiveRoom(data[0]);
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  // Retour à la liste depuis une conversation
  if (activeRoom && rooms.length > 0) {
    return (
      <ConversationView
        room={activeRoom}
        user={user}
        onBack={() => setActiveRoom(null)}
      />
    );
  }

  // Liste des rooms
  const filtered = search
    ? rooms.filter(r => r.room_name?.toLowerCase().includes(search.toLowerCase()))
    : rooms;

  const totalUnread = rooms.reduce((sum, r) => sum + (r.unread_count ?? 0), 0);

  return (
    <div style={{ padding: '0 16px 100px' }}>
      <style>{GLOBAL_STYLE}</style>

      {/* Search — identique à ChatView.js searchTerm */}
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <Search size={15} color="#475569" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search channels…"
          style={{ width: '100%', background: '#1e293b', border: '0.5px solid #334155', borderRadius: 14, padding: '11px 14px 11px 38px', color: '#f1f5f9', fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
        />
      </div>

      {totalUnread > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 13px', background: 'rgba(59,130,246,0.1)', border: '0.5px solid rgba(59,130,246,0.25)', borderRadius: 12, marginBottom: 14 }}>
          <MessageSquare size={14} color="#3b82f6" />
          <span style={{ fontSize: 12, color: '#93c5fd', fontWeight: 600 }}>
            {totalUnread} unread message{totalUnread !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
          <MessageSquare size={36} color="#334155" style={{ marginBottom: 12 }} />
          <p style={{ color: '#475569' }}>No channels found</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {filtered.map(room => (
            <RoomItem
              key={room.id}
              room={room}
              active={activeRoom?.id === room.id}
              onClick={() => setActiveRoom(room)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileStaffChat;